import React, { useId } from 'react';

export const Sparkline = ({ data = [], color = 'var(--itw-cyan)', height = 32, strokeWidth = 1.5 }) => {
  if (!data || data.length < 2) return <div style={{ height }} />;
  const w = 100; const h = height;
  const max = Math.max(...data); const min = Math.min(...data); const r = Math.max(max - min, 1);
  const pts = data.map((v, i) => `${(i / (data.length - 1)) * w},${h - ((v - min) / r) * (h - 4) - 2}`).join(' ');
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{ width: '100%', height }} aria-hidden>
      <polyline points={pts} fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
};

export const AreaChart = ({ data = [], color = 'var(--itw-cyan)', height = 120 }) => {
  const rawId = useId(); const gid = 'g' + rawId.replace(/[^a-zA-Z0-9]/g, '');
  if (!data || data.length < 2) return <div style={{ height }} className="grid place-items-center text-[11px] text-[color:var(--itw-text-3)] font-mono">no data</div>;
  const w = 300; const h = height;
  const max = Math.max(...data); const min = Math.min(...data, 0); const r = Math.max(max - min, 1);
  const step = w / (data.length - 1);
  const coords = data.map((v, i) => `${(i * step).toFixed(1)},${(h - ((v - min) / r) * (h - 10) - 5).toFixed(1)}`);
  const path = `M ${coords.join(' L ')}`;
  const area = `${path} L ${w},${h} L 0,${h} Z`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{ width: '100%', height }} aria-hidden>
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.32" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${gid})`} />
      <path d={path} fill="none" stroke={color} strokeWidth="1.75" strokeLinejoin="round" />
    </svg>
  );
};

export const Gauge = ({ value = 0, max = 100, color = 'var(--itw-cyan)', size = 108, label, unit = '%' }) => {
  const pct = Math.max(0, Math.min(value / max, 1));
  const stroke = 9; const R = size / 2 - stroke; const C = 2 * Math.PI * R;
  const cx = size / 2; const cy = size / 2;
  return (
    <div className="relative grid place-items-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} aria-hidden>
        <circle cx={cx} cy={cy} r={R} stroke="var(--itw-border)" strokeWidth={stroke} fill="none" opacity="0.5" />
        <circle cx={cx} cy={cy} r={R} stroke={color} strokeWidth={stroke} fill="none" strokeLinecap="round"
          strokeDasharray={`${(C * pct).toFixed(1)} ${C.toFixed(1)}`} transform={`rotate(-90 ${cx} ${cy})`}
          style={{ transition: 'stroke-dasharray 0.6s cubic-bezier(0.22,1,0.36,1)' }} />
      </svg>
      <div className="absolute inset-0 grid place-items-center text-center">
        <div>
          <div className="font-mono text-lg font-semibold text-[color:var(--itw-text)] tabular-nums">{Math.round(value)}<span className="text-[11px] text-[color:var(--itw-text-3)]">{unit}</span></div>
          {label && <div className="text-[10px] uppercase tracking-wider text-[color:var(--itw-text-3)] font-mono">{label}</div>}
        </div>
      </div>
    </div>
  );
};

export const Donut = ({ segments = [], size = 120, thickness = 14 }) => {
  const total = segments.reduce((a, s) => a + s.value, 0) || 1;
  const R = size / 2 - thickness; const C = 2 * Math.PI * R; const cx = size / 2; const cy = size / 2;
  let offset = 0;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden>
      <circle cx={cx} cy={cy} r={R} stroke="var(--itw-border)" strokeWidth={thickness} fill="none" opacity="0.4" />
      {segments.map((s, i) => {
        const len = (s.value / total) * C;
        const el = (
          <circle key={i} cx={cx} cy={cy} r={R} stroke={s.color} strokeWidth={thickness} fill="none"
            strokeDasharray={`${len.toFixed(1)} ${(C - len).toFixed(1)}`} strokeDashoffset={(-offset).toFixed(1)}
            transform={`rotate(-90 ${cx} ${cy})`} />
        );
        offset += len; return el;
      })}
    </svg>
  );
};

export const Bars = ({ data = [], color = 'var(--itw-cyan)', height = 60 }) => {
  if (!data.length) return null;
  const max = Math.max(...data, 1);
  return (
    <div className="flex items-end gap-[2px]" style={{ height }}>
      {data.map((v, i) => (
        <div key={i} className="flex-1 rounded-sm" style={{ height: `${(v / max) * 100}%`, background: color, opacity: 0.35 + (v / max) * 0.65, transition: 'height 0.5s ease' }} />
      ))}
    </div>
  );
};
